import { useEffect, useRef } from 'react';

/**
 * Scroll motion layer for the marketing page, ported from the design handoff's
 * inline script (itself a port of apps/oneasia-corp's hook).
 *
 * Four effects, all opted into with data attributes on the markup:
 *   data-reveal             fade + rise once the element enters the viewport
 *   data-reveal-delay="90"  optional ms delay, to stagger a row of siblings
 *   data-scrub="left|right" kinetic chapter word slides in as you scroll
 *   data-parallax="0.05"    image drifts against the scroll direction
 *   data-bm-track           brand moment: pins a card and scrubs two words
 *                           across it (with data-bm-card / data-bm-word)
 *
 * Every effect writes inline transform/opacity, so none of these attributes may
 * sit on an element that also carries a CSS entrance animation — put the
 * entrance on a wrapper and the scroll attribute on a child.
 *
 * The whole layer bails out under `prefers-reduced-motion: reduce`, leaving the
 * static layout.
 */
export function useScrollMotion<T extends HTMLElement = HTMLDivElement>() {
  const rootRef = useRef<T | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const cleanups: Array<() => void> = [];
    const frame = requestAnimationFrame(() => {
      // ---- 1. Scroll reveal ------------------------------------------------
      const revealEls = Array.from(
        root.querySelectorAll<HTMLElement>('[data-reveal]')
      );
      if (revealEls.length) {
        const revealObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const el = entry.target as HTMLElement;
              el.style.opacity = '1';
              el.style.transform = 'none';
              revealObserver.unobserve(el);
            });
          },
          { threshold: 0.15 }
        );

        revealEls.forEach((el) => {
          // Anything already near the top of the fold is never hidden.
          if (el.getBoundingClientRect().top < window.innerHeight * 0.85) return;
          // A grid of siblings all crossing the threshold together reads as one
          // blunt pop; data-reveal-delay staggers them into a run. The delay has
          // to be baked into the shorthand written here — a transition-delay set
          // from CSS or the style prop is reset by this assignment.
          const delay = Number(el.dataset.revealDelay) || 0;
          el.style.transition =
            `opacity 0.8s ease ${delay}ms, ` +
            `transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms`;
          el.style.opacity = '0';
          el.style.transform = 'translateY(48px) scale(0.98)';
          revealObserver.observe(el);
        });

        cleanups.push(() => revealObserver.disconnect());
      }

      // ---- 2 + 3 + 4. Kinetic word scrub, parallax, brand moment -----------
      const scrubEls = Array.from(
        root.querySelectorAll<HTMLElement>('[data-scrub]')
      );
      const parallaxEls = Array.from(
        root.querySelectorAll<HTMLElement>('[data-parallax]')
      );
      // The brand moment's pinned card. Everything it animates is written as a
      // CSS custom property or an inline transform on a composited element —
      // no layout properties, and no React state, per frame.
      const bmTracks = Array.from(
        root.querySelectorAll<HTMLElement>('[data-bm-track]')
      ).map((track) => ({
        track,
        card: track.querySelector<HTMLElement>('[data-bm-card]'),
        img: track.querySelector<HTMLElement>('[data-bm-card] img'),
        wordA: track.querySelector<HTMLElement>('[data-bm-word="a"]'),
        wordB: track.querySelector<HTMLElement>('[data-bm-word="b"]'),
      }));

      if (scrubEls.length || parallaxEls.length || bmTracks.length) {
        let ticking = false;

        const update = () => {
          ticking = false;
          const vh = window.innerHeight;

          // 140px of travel is a gesture on a desktop column and half a phone
          // screen on a handset, so it scales with the viewport below ~780px.
          const scrubDistance = Math.min(140, window.innerWidth * 0.18);

          scrubEls.forEach((el) => {
            const rect = el.getBoundingClientRect();
            const p = Math.min(Math.max((vh - rect.top) / (vh * 0.7), 0), 1);
            const dir = el.dataset.scrub === 'right' ? 1 : -1;
            el.style.transform = `translateX(${dir * (1 - p) * scrubDistance}px)`;
            el.style.opacity = String(0.15 + 0.85 * p);
          });

          parallaxEls.forEach((el) => {
            const amount = Number(el.dataset.parallax) || 0;
            const rect = el.getBoundingClientRect();
            const c = rect.top + rect.height / 2 - vh / 2;
            el.style.transform = `translateY(${-c * amount}px)`;
          });

          bmTracks.forEach(({ track, card, img, wordA, wordB }) => {
            const rect = track.getBoundingClientRect();
            // The stage is 100vh tall inside the track, so the scrub travel is
            // exactly track height - viewport height.
            const len = rect.height - vh;
            const p = Math.min(Math.max(-rect.top / (len || 1), 0), 1);

            card?.style.setProperty('--bs', (0.9 + p * 0.1).toFixed(4));
            // The image counter-scales so it settles as the card grows.
            img?.style.setProperty('--bz', (1.06 - p * 0.06).toFixed(4));

            if (wordA) {
              const e = Math.min(p / 0.55, 1);
              wordA.style.transform =
                `translate3d(${-14 + e * 14}%, ${20 - e * 20}%, 0) ` +
                `scale(${(0.8 + e * 0.2).toFixed(3)})`;
              wordA.style.opacity = e.toFixed(3);
            }

            if (wordB) {
              // Trails the first word by 0.2 of the track.
              const e2 = Math.min(Math.max((p - 0.2) / 0.55, 0), 1);
              wordB.style.transform =
                `translate3d(${16 - e2 * 16}%, ${-18 + e2 * 18}%, 0) ` +
                `scale(${(0.8 + e2 * 0.2).toFixed(3)})`;
              wordB.style.opacity = e2.toFixed(3);
            }
          });
        };

        const onScroll = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(update);
        };

        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        cleanups.push(() => {
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
        });
      }
    });

    return () => {
      cancelAnimationFrame(frame);
      cleanups.forEach((fn) => fn());
    };
  }, []);

  return rootRef;
}

export default useScrollMotion;
